'use client';

import { KeyVisualFootnotes } from '../partials';
import type { KeyVisualDownloadProps } from '../types';
import { KeyVisualClassic } from './KeyVisualClassic';
import { useDeviceInfo, useSoftwareDownload } from 'hooks';
import { OPERATING_SYSTEM } from 'utils';
import { useEffect } from 'react';

/**
 * KeyVisualDownload component - Download page variant for key visuals.
 *
 * Wraps KeyVisualClassic and adds footnotes with icons below the action buttons.
 * This variant is specifically for software download pages.
 *
 * Footnotes are rendered using the KeyVisualFootnotes partial component,
 * which uses the centralized icon registry (getIconByName) for consistency.
 *
 * @param props - KeyVisual variant component properties
 * @returns Rendered download key visual variant using KeyVisualClassic with footnotes
 *
 * @example
 * <KeyVisualDownload
 *   keyVisual={{
 *     title: "Thank you for your purchase!",
 *     subtitle: "Download your software",
 *     footnotes: [
 *       { sys: { id: '1' }, text: "Virus-checked", asset: "verified" },
 *       { sys: { id: '2' }, text: "Free", asset: "free" }
 *     ],
 *     // ... other fields
 *   }}
 *   device="desktop"
 *   awards={[]}
 * />
 */
export function KeyVisualDownload(props: KeyVisualDownloadProps) {
  const {
    keyVisual: { footnotes = [] },
  } = props;
  const deviceInfo = useDeviceInfo();
  const triggerDownload = useSoftwareDownload(deviceInfo.operatingSystem);

  useEffect(() => {
    // PSI needs a supported OS, and iOS has no desktop HPS download.
    if (
      deviceInfo.operatingSystem === OPERATING_SYSTEM.IOS ||
      deviceInfo.operatingSystem === OPERATING_SYSTEM.UNKNOWN
    ) {
      return;
    }

    // Auto-trigger software download on page load with correct OS
    // Uses async IIFE (Immediately Invoked Function Expression) to properly handle
    // the Promise returned by triggerDownload without blocking the render
    (async () => {
      await triggerDownload();
    })();
  }, [deviceInfo.operatingSystem, triggerDownload]);

  return (
    <div className="key-visual-download">
      <KeyVisualClassic {...props} footNotes={<KeyVisualFootnotes footnotes={footnotes} />} />
    </div>
  );
}
